/**
 * "Add your account ending {mask}?" (TMMAPP-0119 item 25, TMMAPI-0157): the derived
 * `MISSING_ACCOUNT` review item, its own card above the paybacks. Several transfer lines name an
 * account the person hasn't added. Add account opens the add-account form in a dialog with the last
 * four prefilled (the person chooses managed or unmanaged); once it is created, the same dialog moves
 * to the confirm step, which claims the lines (`claim-transfer-lines`) and, ticked by default, adds a
 * rule so future ones classify themselves. Dismiss hides the suggestion. The source is DETERMINISTIC:
 * no AI pill (TMM-B9).
 *
 * TMMAPP-0136: an identity key is never shown (a wire item reads "at {bank}" or "abroad"); the
 * dialog is titled "Add account", starts on Checking, and pre-fills Last 4 only from a 4-digit
 * mask. Acting on the item closes it, and a dismissal the refetch still serves says so.
 *
 * TMMAPP-0138: the dialog is the shared own-account flow (`OwnAccountDialog`). Dismiss means "not
 * mine" (TMMAPI-0174 §F): the dismissal releases the lines to payee suggestions, and says so.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { Alert, Box, Button, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { Account } from '@/api/accounts';
import { errorCopy } from '@/api/errorCopy';
import { reviewApi, type ReviewItem } from '@/api/review';
import {
  compactIdentifier,
  prefillLast4,
  servedIdentifierKind,
} from '@/features/accounts/identifierDisplay';
import { transfersApi, type ClaimTransferLinesResult } from '@/api/transfers';
import { OwnAccountDialog, type OwnAccountStart } from '@/features/accounts/OwnAccountDialog';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { SubscriberActionHint, useSubscriberAction } from '@/session/useSubscriberAction';
import { useNotify } from '@/shell/NotifyProvider';
import { resolveTokens } from '@/theme/theme';

interface MissingAccountCardProps {
  item: ReviewItem;
  /** The person's accounts: the sample line's account is the form's source (its currency). */
  accounts: Account[];
  operatingCurrency: string | null;
  /** The queue reloads after a claim or a dismissal: the item leaves it. */
  onChanged: () => void | Promise<void>;
  /** Opens a skipped line's drawer (the toast's link). */
  onOpenLine: (transactionId: number) => void;
}

const FOUR_DIGITS = /^\d{4}$/;
/** A BIC (8 or 11 characters) or a 9-digit routing number: a code, never shown as the bank. */
const BANK_CODE = /^(?:[A-Z]{6}[A-Z0-9]{2}(?:[A-Z0-9]{3})?|\d{9})$/;

/**
 * A wire item (TMMAPI-0165): self-named, keyed by a wire identity (`NAME:` / `ACCT:`), or carrying a
 * served identifier. Anything but a 4-digit mask is never "ending {mask}" (TMMAPP-0136 item 1).
 */
function isWireItem(item: ReviewItem): boolean {
  return (
    item.evidence === 'SELF_NAME' ||
    !FOUR_DIGITS.test(item.mask ?? '') ||
    (item.identifierKind !== null && item.identifierKind !== 'MASK' && item.identifier !== null)
  );
}

/**
 * The served bank's name (item 2): TMMAPI-0170 serves the name as `counterpartyBank` and the BIC or
 * routing id as `counterpartyBankId`. Null when absent or only a code (an item served before 0170).
 */
function bankName(
  item: Pick<ReviewItem, 'counterpartyBank' | 'counterpartyBankId'>,
): string | null {
  const bank = item.counterpartyBank?.trim() ?? '';
  if (bank.length === 0 || bank === item.counterpartyBankId) return null;
  return item.counterpartyBankId === null && BANK_CODE.test(bank) ? null : bank;
}

/** The identifier to pre-fill: an account number, IBAN or CLABE, never a name key (item 2). */
function prefillIdentifier(item: ReviewItem): string {
  return servedIdentifierKind(item.identifierKind) !== null && item.identifier !== null
    ? item.identifier
    : '';
}

/** The card's title. The raw `mask` shows only as a 4-digit "ending {mask}" (item 1). */
function itemTitle(item: ReviewItem, wire: boolean, t: TFunction): string {
  const bank = bankName(item);
  if (item.identifierDisplay !== null && item.identifierDisplay.length > 0) {
    return bank !== null
      ? t('review.missingAccount.identifierTitleAt', {
          identifier: item.identifierDisplay,
          bank,
        })
      : t('review.missingAccount.identifierTitle', { identifier: item.identifierDisplay });
  }
  if (wire) {
    return bank !== null
      ? t('review.missingAccount.titleAt', { bank })
      : t('review.missingAccount.titleAbroad');
  }
  return t('review.missingAccount.title', { mask: item.mask ?? '' });
}

export function MissingAccountCard({
  item,
  accounts,
  operatingCurrency,
  onChanged,
  onOpenLine,
}: MissingAccountCardProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const notify = useNotify();
  const headingId = useId();
  const notMineId = useId();
  const mask = item.mask ?? '';
  const count = item.lineCount ?? 0;
  const wireItem = isWireItem(item);
  const title = itemTitle(item, wireItem, t);
  const [start, setStart] = useState<OwnAccountStart | null>(null);
  // The account this card created (TMMAPP-0122 item 15): from then on the card only confirms.
  const [created, setCreated] = useState<Account | null>(null);
  // An account already ending in the mask (a remount after it was created, or added elsewhere;
  // TMMAPP-0124 item 11): the card confirms with it and never offers Add account.
  const existing =
    created ??
    accounts.find((account) => {
      if (account.access !== 'OWNER') return false;
      if (wireItem && item.identifier !== null) {
        return (
          compactIdentifier(account.externalIdentifier ?? '') === compactIdentifier(item.identifier)
        );
      }
      return mask.length > 0 && account.last4 === mask;
    }) ??
    null;
  const [dismissError, setDismissError] = useState<string | null>(null);
  // Set once a dismissal and the queue's refetch have both finished. The card is keyed by its
  // mask, so it is still mounted only when the item is still served: it says so (TMMAPP-0136 item 3).
  const [stuck, setStuck] = useState(false);
  // Dismiss sends one dismissal at a time (item 17).
  const dismissing = useRef(false);
  // A dismissal that stuck: the card leaves the queue, and says where the lines go as it does
  // (TMMAPI-0174 §F, TMMAPP-0138 item 7). One the refetch still serves stays, "didn't stick".
  const dismissed = useRef(false);
  useEffect(
    () => () => {
      if (dismissed.current) notify(t('review.missingAccount.dismissedNotMine'));
    },
    [notify, t],
  );
  // Still mounted with "didn't stick" showing: that dismissal never left the queue, so a later
  // unmount says nothing.
  useEffect(() => {
    if (stuck) dismissed.current = false;
  }, [stuck]);

  /** Dismisses the item by its key (the body form, TMMAPI-0170). */
  async function dismissNow() {
    if (dismissing.current) return;
    dismissing.current = true;
    setDismissError(null);
    setStuck(false);
    try {
      await reviewApi.dismissMissingAccount(mask);
      dismissed.current = true;
      await onChanged();
      setStuck(true);
    } catch (error) {
      setDismissError(errorCopy(t, error, 'planned.errorGeneric'));
    } finally {
      dismissing.current = false;
    }
  }

  /**
   * The claim, in the shared own-account flow (TMMAPP-0138 item 7): a 4-digit key claims by
   * `{mask}`; a wire item's identity key by `{identityKey}` (TMMAPI-0170), so the account remembers
   * it, the item closes and future lines classify themselves.
   */
  function claim(account: Account, createRules: boolean): Promise<ClaimTransferLinesResult> {
    return FOUR_DIGITS.test(mask)
      ? transfersApi.claimTransferLines(account.id, mask, createRules)
      : transfersApi.claimIdentity(account.id, mask, createRules);
  }

  // One gate for both actions: the button names its action in `data-missing-action`.
  const gate = useSubscriberAction((event) => {
    if (event.currentTarget.dataset.missingAction === 'add') {
      setStuck(false);
      // A wire item claims at once with rules; a 4-digit item confirms first.
      if (existing !== null) {
        setStart(
          wireItem ? { kind: 'claim', account: existing } : { kind: 'confirm', account: existing },
        );
        return;
      }
      setStart({ kind: 'form' });
      return;
    }
    void dismissNow();
  });
  // Closed without a claim (Not now, Esc, after a refused claim): once the account exists, the
  // queue and the accounts reload, and the card reopens at the confirm step.
  const close = () => {
    setStart(null);
    if (existing !== null) void onChanged();
  };
  const sampleAccount = item.sampleLine?.accountName ?? null;
  const source =
    accounts.find((account) => account.access === 'OWNER' && account.name === sampleAccount) ??
    null;
  const sample = item.sampleLine?.payee ?? '';

  return (
    <Box
      role="group"
      aria-labelledby={headingId}
      data-missing-account={mask}
      sx={{
        p: 2,
        bgcolor: tokens.surface,
        border: `1px solid ${tokens.line}`,
        borderRadius: `${tokens.radius.card}px`,
        boxShadow: tokens.elevation.card,
      }}
    >
      <Stack spacing={1}>
        <Typography variant="h3" id={headingId}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: tokens.text2, overflowWrap: 'anywhere' }}>
          {item.evidence === 'SELF_NAME'
            ? t('review.missingAccount.selfName', { count, sample })
            : t(wireItem ? 'review.missingAccount.bodyWire' : 'review.missingAccount.body', {
                count,
                firstOn: item.firstOn !== null ? formatCivilDate(item.firstOn, locale) : '—',
                lastOn: item.lastOn !== null ? formatCivilDate(item.lastOn, locale) : '—',
                mask,
                sample,
              })}
        </Typography>
        {dismissError !== null && <Alert severity="error">{dismissError}</Alert>}
        {stuck && dismissError === null && (
          <Alert severity="error" data-missing-stuck="">
            {t('review.missingAccount.didntStick')}
          </Alert>
        )}
        <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="small"
            data-missing-action="add"
            aria-describedby={gate.descriptionId}
            onClick={gate.run}
          >
            {existing !== null
              ? t('review.missingAccount.useExisting', { account: existing.name })
              : t('review.missingAccount.add')}
          </Button>
          {/* Dismiss means "not mine" (TMMAPI-0174 §F): the lines go to payee suggestions. */}
          <Tooltip title={t('review.missingAccount.dismissHint')} describeChild>
            <Button
              size="small"
              data-missing-action="dismiss"
              aria-describedby={[notMineId, gate.descriptionId].filter(Boolean).join(' ')}
              onClick={gate.run}
            >
              {t('review.missingAccount.dismiss')}
            </Button>
          </Tooltip>
        </Stack>
        <Box component="span" id={notMineId} sx={visuallyHidden}>
          {t('review.missingAccount.dismissHint')}
        </Box>
        <SubscriberActionHint id={gate.descriptionId} description={gate.description} />
      </Stack>
      {start !== null && (
        <OwnAccountDialog
          title={t('transfer.addAccount')}
          start={start}
          source={source}
          mask={mask}
          operatingCurrency={operatingCurrency}
          form={{
            initialSubtype: 'CHECKING',
            showHeading: false,
            // A 4-digit mask, else the served identifier's last four digits (TMMAPP-0139 item 7).
            initialLast4: prefillLast4(mask, prefillIdentifier(item)),
            initialIdentifier: prefillIdentifier(item),
            initialInstitution: bankName(item) ?? '',
          }}
          confirmTitle={
            wireItem
              ? undefined
              : (account) =>
                  t('review.missingAccount.confirmTitle', { count, account: account.name })
          }
          claim={claim}
          onCreated={setCreated}
          onNotify={notify}
          onClose={close}
          onClaimed={async (result) => {
            setStart(null);
            if (wireItem) {
              notify(t('review.missingAccount.transfersNow', { count: result.classified }));
              await onChanged();
              return;
            }
            const first = result.skipped[0];
            // One sentence per outcome, each a catalog key carrying its counts (item 19).
            const message =
              result.skipped.length > 0
                ? t('review.missingAccount.classifiedAndSkipped', {
                    classified: result.classified,
                    count: result.skipped.length,
                  })
                : t('review.missingAccount.classified', { count: result.classified });
            notify(message, {
              severity: 'success',
              ...(first !== undefined
                ? {
                    action: {
                      label: t('review.missingAccount.openSkipped'),
                      onClick: () => onOpenLine(first.transactionId),
                    },
                  }
                : {}),
            });
            await onChanged();
          }}
        />
      )}
    </Box>
  );
}
